/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable sonarjs/cognitive-complexity */
'use client';
import { formatCurrency } from '@ahm/common-helpers';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  rem,
  Text,
} from '@mantine/core';
import { useTranslations } from 'next-intl';
import React from 'react';
import classes from '../loading-goods-modal.module.css';
import ModalFooter from '@/components/modals/components/modal-footer';

type DistanceOption = string;

interface DistanceButtonProps {
  value: DistanceOption;
  label: string;
  selectedValue: DistanceOption;
  onChange: (
    value: DistanceOption,
    type: 'pickupHelperConfig' | 'deliveryHelperConfig'
  ) => void;
  isMobile?: boolean;
  type: 'pickupHelperConfig' | 'deliveryHelperConfig';
}

const DistanceButton = ({
  value,
  label,
  selectedValue,
  onChange,
  isMobile,
  type,
}: DistanceButtonProps) => {
  const isSelected = selectedValue === value;

  const handleSelectDistance = () => {
    onChange(isSelected ? '' : value, type);
  };

  return (
    <Button
      miw={isMobile ? rem(110) : rem(150)}
      style={{
        borderColor: isSelected
          ? 'var(--mantine-color-primary-6)'
          : 'var(--mantine-color-neutral-4)',
        backgroundColor: isSelected
          ? 'var(--mantine-color-primary-2)'
          : 'transparent',
        whiteSpace: 'nowrap',
      }}
      fw={isSelected ? 600 : 500}
      radius={rem(40)}
      variant={isSelected ? 'filled' : 'outline'}
      c={isSelected ? 'primary.6' : 'neutral.7'}
      onClick={handleSelectDistance}
    >
      <Text size={isMobile ? 'sm' : 'md'} fw={isSelected ? 600 : 500}>
        {label}
      </Text>
    </Button>
  );
};

interface LoadingGoodsItemProps {
  title: string;
  description?: string;
  distanceOptions: any[];
  selectedHelperConfig: {
    distance: string;
    additionalOptions?: Record<string, boolean>;
  };
  onDistanceChange: (
    value: string,
    type?: 'pickupHelperConfig' | 'deliveryHelperConfig'
  ) => void;
  checkboxOptions: any[];
  onCheckboxChange: (
    optionCode: string,
    type?: 'pickupHelperConfig' | 'deliveryHelperConfig'
  ) => void;
  isOptionDisabled: (optionCode: string, selectedDistance: string) => boolean;
  isMobile?: boolean;
  icon?: React.ReactNode;
  type: 'pickupHelperConfig' | 'deliveryHelperConfig';
  onClose?: () => void;
  onConfirm?: () => void; // Thêm prop onConfirm
}

const LoadingGoodsItem = ({
  title,
  description,
  distanceOptions,
  selectedHelperConfig,
  onDistanceChange,
  checkboxOptions,
  onCheckboxChange,
  isOptionDisabled,
  isMobile,
  icon,
  type,
  onConfirm,
}: LoadingGoodsItemProps) => {
  const tCommon = useTranslations('common');
  const selectedDistance = selectedHelperConfig.distance;
  const selectedCheckboxOptions = selectedHelperConfig.additionalOptions || {};

  const calculatedFeePerOption = () => {
    let total = 0;

    const pickupOption = distanceOptions.find(
      (opt) => opt.value === selectedDistance
    );
    if (pickupOption) {
      total += pickupOption.price;
    }

    const additionalOptions = selectedCheckboxOptions || {};
    Object.entries(additionalOptions).forEach(([optionCode, isSelected]) => {
      if (isSelected) {
        const optionGroup = checkboxOptions.find(
          (group) => group.code === optionCode
        );
        if (optionGroup?.price) {
          total += optionGroup.price;
        }
      }
    });

    return total;
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  return (
    <>
      {isMobile ? null : (
        <Card.Section>
          <Group
            p={rem(16)}
            style={{
              borderBottom: '1px solid var(--mantine-color-neutral-1)',
            }}
            gap={rem(8)}
          >
            {icon}

            <Box>
              <Text size="md" fw={700} span>
                {title}{' '}
                {description && (
                  <Text lineClamp={1} c="neutral.5" size="sm" fw={500} span>
                    ({description})
                  </Text>
                )}
              </Text>
            </Box>
          </Group>
        </Card.Section>
      )}

      <Box pb={isMobile ? rem(115) : rem(0)}>
        <Text
          lineClamp={1}
          fw={600}
          size="sm"
          mt={isMobile ? 0 : rem(16)}
          mb={rem(12)}
        >
          Phạm vi hỗ trợ (tính từ đuôi xe)
        </Text>

        <Group gap={isMobile ? 8 : 12} align="flex-start">
          {distanceOptions.map((option) => (
            <DistanceButton
              type={type}
              key={option.value}
              value={option.value}
              label={option.label}
              selectedValue={selectedDistance}
              onChange={onDistanceChange}
              isMobile={isMobile}
            />
          ))}
        </Group>

        {selectedDistance && (
          <>
            {/* Render checkbox options */}
            <Card.Section>
              {checkboxOptions.map((optionGroup, index) => {
                const shouldHideGroup = isOptionDisabled(
                  optionGroup.code,
                  selectedDistance
                );

                if (shouldHideGroup) return null;

                return (
                  <>
                    <Divider mt={rem(index ? 0 : 16)} />
                    <Checkbox.Card
                      className={classes.checkboxCard}
                      key={optionGroup.code}
                      checked={
                        selectedCheckboxOptions[optionGroup.code] || false
                      }
                      onClick={() => onCheckboxChange(optionGroup.code, type)}
                      withBorder={false}
                    >
                      <Group
                        w="100%"
                        mih={rem(64)}
                        px={rem(16)}
                        variant="subtle"
                        color="neutral"
                        wrap="nowrap"
                        align="center"
                        justify="space-between"
                      >
                        <Box>
                          <Text lineClamp={1} size="md" fw={600}>
                            {optionGroup.name}
                          </Text>
                          <Text lineClamp={1} size="xs" c="dimmed">
                            {optionGroup.descVn}
                          </Text>
                        </Box>
                        <Checkbox.Indicator />
                      </Group>
                    </Checkbox.Card>
                  </>
                );
              })}
            </Card.Section>
          </>
        )}
      </Box>

      {isMobile && (
        <ModalFooter>
          <Group w="100%" gap={rem(8)} justify="space-between" align="center">
            <Text size="sm" c="neutral.6">
              Chi phí bốc xếp
            </Text>
            <Text size="md" c="primary.6" fw={700}>
              {formatCurrency(calculatedFeePerOption())}
            </Text>
          </Group>
          <Button fullWidth size="md" mih={rem(48)} onClick={handleConfirm}>
            {tCommon('confirm')}
          </Button>
        </ModalFooter>
      )}
    </>
  );
};

export default LoadingGoodsItem;
